import { useMemo } from "react";
import { X, Crosshair, Trash2 } from "lucide-react";
import { compareColor } from "@/components/HistoryMap";

// V1 comparaison — 100% sur les trajets déjà en mémoire (/fleet-trips). Aucune requête, aucune métrique inventée.
// Structuré pour extension future (similarité, divergence, segments, relecture, export PDF) sans refonte.
const nf1 = (n) => (Math.round(n * 10) / 10).toLocaleString("fr-FR", { minimumFractionDigits: 0, maximumFractionDigits: 1 });
const hm = (iso) => (iso ? new Date(iso).toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" }) : "—");
const dmy = (iso) => (iso ? new Date(iso).toLocaleDateString("fr-FR", { day: "2-digit", month: "2-digit", year: "numeric" }) : "—");
const durTxt = (min) => (min == null ? "—" : (min >= 60 ? `${Math.floor(min / 60)} h ${Math.round(min % 60)}` : `${Math.round(min)} min`));

function tripMetrics(t) {
  const stops = (t.events || []).filter((e) => e.type === "stop" || e.type === "idle");
  const stopMin = stops.reduce((s, e) => s + (e.duration_min || 0), 0);
  const dist = t.distance_km ?? null;
  const dur = t.duration_min ?? null;
  const avg = (dist != null && dur > 0) ? (dist / (dur / 60)) : null; // dérivé de données présentes, non inventé
  return {
    dist, dur, avg,
    maxsp: t.max_speed ?? null,
    nstops: (t.events && t.events.length != null) ? stops.length : null,
    stopMin: (t.events && t.events.length != null) ? stopMin : null,
  };
}

function Delta({ v, ref, unit, decimals }) {
  if (v == null || ref == null) return null;
  const d = v - ref;
  if (Math.abs(d) < (decimals ? 0.05 : 0.5)) return <span className="text-muted-foreground text-[10px]">±0</span>;
  const s = d > 0 ? "+" : "−";
  const val = decimals ? nf1(Math.abs(d)) : Math.round(Math.abs(d)).toString();
  return <span className={`text-[10px] font-semibold ${d > 0 ? "text-[#F5821F]" : "text-[#17A2B8]"}`}>{s}{val}{unit ? ` ${unit}` : ""}</span>;
}

export default function ComparePanel({ trips, idxs, onClose, onHover, onRecenter, onRemove }) {
  const cols = useMemo(() => idxs.map((ti, pos) => ({ ti, pos, t: trips[ti], m: tripMetrics(trips[ti]) })).filter((c) => c.t), [idxs, trips]);
  if (!cols.length) return null;
  const ref = cols[0].m;

  const Row = ({ label, render, numeric, pick, unit, decimals }) => (
    <tr className="border-t border-border/50">
      <td className="py-1.5 pr-3 label-mono text-[10px] whitespace-nowrap sticky left-0 bg-card z-10">{label}</td>
      {cols.map((c, i) => (
        <td key={c.ti} className="py-1.5 px-3 text-xs whitespace-nowrap" data-testid={`compare-cell-${label}-${c.ti}`}>
          <div className="font-semibold">{render(c)}</div>
          {numeric && i > 0 && <Delta v={pick(c.m)} ref={pick(ref)} unit={unit} decimals={decimals} />}
        </td>
      ))}
    </tr>
  );

  return (
    <div className="absolute inset-x-0 bottom-0 z-[600] glass border-t border-border rounded-t-xl shadow-2xl max-h-[55vh] flex flex-col" data-testid="compare-panel">
      <div className="flex items-center justify-between px-3 py-2 border-b border-border/60 shrink-0">
        <span className="text-sm font-bold flex items-center gap-2"><Crosshair className="w-4 h-4 text-[#17A2B8]" />Comparaison de trajets · réf. = 1er</span>
        <button type="button" onClick={onClose} data-testid="compare-panel-close" className="p-1 rounded hover:bg-white/10"><X className="w-4 h-4" /></button>
      </div>
      <div className="overflow-auto">
        <table className="w-full text-left border-collapse">
          <thead>
            <tr>
              <th className="py-2 pr-3 label-mono text-[10px] sticky left-0 bg-card z-10">Trajet</th>
              {cols.map((c) => (
                <th key={c.ti} className="py-2 px-3 min-w-[150px]"
                  onMouseEnter={() => onHover(c.ti)} onMouseLeave={() => onHover(null)} data-testid={`compare-col-${c.ti}`}>
                  <div className="flex items-center gap-1.5">
                    <span className="w-3 h-3 rounded-full shrink-0" style={{ background: compareColor(c.pos) }} />
                    <span className="font-bold text-xs truncate">{c.t.vehicle_name}</span>
                    {c.pos === 0 && <span className="text-[9px] font-bold text-[#17A2B8] border border-[#17A2B8]/50 rounded px-1">RÉF</span>}
                  </div>
                  <div className="flex items-center gap-2 mt-1">
                    <button type="button" onClick={() => onRecenter(c.ti)} data-testid={`compare-recenter-${c.ti}`} className="text-[10px] text-[#2ec5db] hover:underline flex items-center gap-1"><Crosshair className="w-3 h-3" />Centrer</button>
                    <button type="button" onClick={() => onRemove(c.ti)} data-testid={`compare-remove-${c.ti}`} className="text-[10px] text-[#EF4444] hover:underline flex items-center gap-1"><Trash2 className="w-3 h-3" />Retirer</button>
                  </div>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            <Row label="Immat." render={(c) => c.t.plate || "—"} />
            <Row label="Conducteur" render={(c) => c.t.driver_name || "—"} />
            <Row label="Date" render={(c) => dmy(c.t.start)} />
            <Row label="Départ" render={(c) => hm(c.t.start)} />
            <Row label="Arrivée" render={(c) => hm(c.t.end)} />
            <Row label="Distance" numeric decimals unit="km" pick={(m) => m.dist} render={(c) => c.m.dist != null ? `${nf1(c.m.dist)} km` : "—"} />
            <Row label="Durée" numeric unit="min" pick={(m) => m.dur} render={(c) => durTxt(c.m.dur)} />
            <Row label="Vitesse moy." numeric unit="km/h" pick={(m) => m.avg} render={(c) => c.m.avg != null ? `${Math.round(c.m.avg)} km/h` : "—"} />
            <Row label="Vitesse max." numeric unit="km/h" pick={(m) => m.maxsp} render={(c) => c.m.maxsp != null ? `${c.m.maxsp} km/h` : "—"} />
            <Row label="Arrêts" numeric pick={(m) => m.nstops} render={(c) => c.m.nstops != null ? c.m.nstops : "—"} />
            <Row label="Durée arrêts" numeric unit="min" pick={(m) => m.stopMin} render={(c) => c.m.stopMin != null ? durTxt(c.m.stopMin) : "—"} />
          </tbody>
        </table>
      </div>
    </div>
  );
}
